/**
 * The quote journey as a checklist, not a corridor: every step is derived
 * from the RFQ record, any step can be opened at any time, and the one that
 * needs attention next is marked. Pure, so the strip stays thin and testable.
 */
import type { RfqLine, RfqRecord } from "./rfqApi";
import { rfqDisplayStatus } from "./rfqEvidence";
import { effectiveFindings } from "./rfqUnderstanding";

export type Locale = "en" | "tr";

export type JourneyKey =
  | "understood"
  | "decide"
  | "parameters"
  | "supply"
  | "cost"
  | "offer"
  | "outcome";

export type JourneyState = "done" | "current" | "todo" | "blocked";

export interface JourneyStep {
  key: JourneyKey;
  label: string;
  /** One short line under the label: what is left, or what was decided. */
  detail: string;
  state: JourneyState;
}

export const JOURNEY_KEYS: readonly JourneyKey[] = [
  "understood",
  "supply",
  "decide",
  "parameters",
  "cost",
  "offer",
  "outcome",
] as const;

export const QUOTE_STEPS: readonly JourneyKey[] = ["offer", "outcome"] as const;

/** URL third-segments that name a journey step, including the older aliases. */
const STEP_ALIAS: Record<string, JourneyKey> = {
  understand: "understood",
  understood: "understood",
  decide: "decide",
  lines: "decide",
  parameters: "parameters",
  supply: "supply",
  quote: "offer",
  cost: "cost",
  lead: "offer",
  margin: "offer",
  offer: "offer",
  document: "offer",
  send: "offer",
  outcome: "outcome",
};

export function parseRfqStep(sub: string | null | undefined): JourneyKey | null {
  if (!sub) return null;
  return STEP_ALIAS[sub] ?? null;
}

/** Outcome is linked only while the journey router still names that step. */
export function outcomeRouteReady(): boolean {
  return parseRfqStep("outcome") === "outcome";
}

export function isQuoteStep(key: JourneyKey): boolean {
  return (QUOTE_STEPS as readonly string[]).includes(key);
}

const LABEL: Record<Locale, Record<JourneyKey, string>> = {
  en: {
    understood: "Files",
    decide: "BOM",
    parameters: "Parameters",
    supply: "Stock matching",
    cost: "Cost",
    offer: "Quote",
    outcome: "Outcome",
  },
  tr: {
    understood: "Dosyalar",
    decide: "BOM",
    parameters: "Parametreler",
    supply: "Stok eşleştirme",
    cost: "Maliyet",
    offer: "Teklif",
    outcome: "Sonuç",
  },
};

const DETAIL = {
  en: {
    blockers: (n: number) => `${n} blocking gap${n === 1 ? "" : "s"}`,
    waiting: "waiting for the customer",
    read: "read and confirmed",
    check: "still to confirm",
    noLines: "no lines yet",
    undecided: (n: number) => `${n} undecided`,
    decided: (make: number, buy: number) => `${make} make · ${buy} buy`,
    noBuy: "nothing bought",
    supplierWaiting: (n: number) => `${n} awaiting supplier`,
    supplierAsk: (n: number) => `${n} not asked yet`,
    supplied: "prices in",
    uncosted: (n: number) => `${n} uncosted`,
    noLead: "no lead time yet",
    late: (d: number, by: number) => `${d} d · ${by} d late`,
    marginTodo: (s: number | null) => (s == null ? "not set" : `${s}% suggested`),
    packed: (total: string, lead: number, margin: number) => `${total} · ${lead} d · ${margin}%`,
    docTodo: "not sent",
    docReady: "ready to send",
    docSent: "sent",
    followUp: (days: number) => `sent ${days} d ago · follow up`,
    open: "open",
    won: "won",
    lost: "lost",
    cancelled: "cancelled",
  },
  tr: {
    blockers: (n: number) => `${n} engelleyici eksik`,
    waiting: "müşteri cevabı bekleniyor",
    read: "okundu ve onaylandı",
    check: "onay bekliyor",
    noLines: "henüz kalem yok",
    undecided: (n: number) => `${n} kararsız`,
    decided: (make: number, buy: number) => `${make} yap · ${buy} al`,
    noBuy: "dışarıdan alım yok",
    supplierWaiting: (n: number) => `${n} tedarikçi bekleniyor`,
    supplierAsk: (n: number) => `${n} henüz sorulmadı`,
    supplied: "fiyatlar geldi",
    uncosted: (n: number) => `${n} maliyetsiz`,
    noLead: "termin yok",
    late: (d: number, by: number) => `${d} g · ${by} g geç`,
    marginTodo: (s: number | null) => (s == null ? "girilmedi" : `%${s} önerildi`),
    packed: (total: string, lead: number, margin: number) => `${total} · ${lead} g · %${margin}`,
    docTodo: "gönderilmedi",
    docReady: "gönderime hazır",
    docSent: "gönderildi",
    followUp: (days: number) => `${days} g önce gönderildi · takip et`,
    open: "açık",
    won: "kazanıldı",
    lost: "kaybedildi",
    cancelled: "iptal",
  },
} as const;

/** Days from `sent_at` to now; null when unknown. */
export function daysSince(iso: string | null | undefined, now = Date.now()): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.floor((now - t) / 86_400_000);
}

/** Working days the quote needs, against the customer's date. */
export function leadFit(rfq: RfqRecord, now = Date.now()): { lead: number | null; lateBy: number | null } {
  const lead = rfq.est_lead_days ?? maxLineLead(rfq.lines ?? []);
  if (lead == null || !rfq.due_at) return { lead, lateBy: null };
  const due = Date.parse(rfq.due_at);
  if (!Number.isFinite(due)) return { lead, lateBy: null };
  const available = Math.floor((due - now) / 86_400_000);
  return { lead, lateBy: Math.max(0, lead - available) };
}

function maxLineLead(lines: RfqLine[]): number | null {
  let max: number | null = null;
  for (const l of lines) {
    const own = l.est_lead_days ?? l.supplier_ask?.reply_lead_days ?? null;
    if (own != null && (max == null || own > max)) max = own;
  }
  return max;
}

export const FOLLOW_UP_AFTER_DAYS = 3;

export function journeySteps(
  rfq: RfqRecord,
  locale: Locale,
  opts: { money?: (v: number) => string; now?: number } = {},
): JourneyStep[] {
  const L = LABEL[locale];
  const D = DETAIL[locale];
  const money = opts.money ?? ((v: number) => `${Math.round(v)} ₺`);
  const now = opts.now ?? Date.now();
  const lines = rfq.lines ?? [];
  const blockers = effectiveFindings(rfq).filter((f) => f.severity === "blocker");
  const status = rfqDisplayStatus(rfq);
  const closed = status === "won" || status === "lost" || status === "cancelled";
  const sent = status === "sent" || closed;

  const steps: JourneyStep[] = [];

  // 1 · understood
  const understood: JourneyStep = {
    key: "understood",
    label: L.understood,
    state: "todo",
    detail: D.noLines,
  };
  if (status === "waiting_customer") {
    understood.state = "current";
    understood.detail = D.waiting;
  } else if (lines.length === 0) {
    understood.state = "current";
  } else if (blockers.length) {
    understood.state = "blocked";
    understood.detail = D.blockers(blockers.length);
  } else if ((rfq.status === "ready" || sent) && status !== "draft") {
    // Settled only once the stored status is ready or sent. A file-less
    // quote-like status is shown as draft, so it stays on "check".
    understood.state = "done";
    understood.detail = D.read;
  } else {
    understood.state = "current";
    understood.detail = D.check;
  }
  steps.push(understood);

  const pendingDecide = lines.filter((l) => l.decision_status !== "decided" && l.decision_status !== "skipped").length;
  const make = lines.filter((l) => l.make_buy === "make" || l.make_buy === "mixed").length;
  const buyLines = lines.filter((l) => l.make_buy === "buy" || l.make_buy === "outsource");
  const filesClosed = rfq.status !== "draft" && rfq.status !== "waiting_customer" && status !== "draft";
  const approved = (rfq.status === "ready" || sent) && status !== "draft";

  // 2 · stock matching, before BOM. A suggested route is not an approval.
  const notAsked = buyLines.filter((l) => !l.supplier_ask || l.supplier_ask.status === "draft").length;
  const awaiting = buyLines.filter((l) => l.supplier_ask?.status === "waiting").length;
  const supplySettled = buyLines.length > 0 && notAsked === 0 && awaiting === 0;
  steps.push({
    key: "supply",
    label: L.supply,
    state: supplySettled && approved ? "done" : buyLines.length === 0 ? "todo" : "current",
    detail:
      buyLines.length === 0
        ? D.noBuy
        : notAsked
          ? D.supplierAsk(notAsked)
          : awaiting
            ? D.supplierWaiting(awaiting)
            : D.supplied,
  });

  // 3 · BOM. A suggested make/buy is not a decision.
  steps.push({
    key: "decide",
    label: L.decide,
    state: lines.length === 0 ? "todo" : approved && pendingDecide === 0 ? "done" : pendingDecide > 0 && filesClosed ? "current" : "todo",
    detail: lines.length === 0 ? D.noLines : pendingDecide ? D.undecided(pendingDecide) : D.decided(make, buyLines.length),
  });

  steps.push({
    key: "parameters",
    label: L.parameters,
    state: lines.length === 0 ? "todo" : rfq.due_at ? "done" : "todo",
    detail: rfq.due_at ? rfq.due_at.slice(0, 10) : "",
  });

  const uncostedLines = lines.filter((line) => line.est_cost_try == null).length;
  const costApproved = approved;
  steps.push({
    key: "cost",
    label: L.cost,
    state: lines.length === 0 ? "todo" : costApproved && uncostedLines === 0 ? "done" : "current",
    detail: lines.length === 0 ? "" : uncostedLines > 0 ? String(uncostedLines) : "",
  });

  // 4 · offer (cost + lead + margin as one packet)
  const uncosted = lines.filter((l) => l.est_cost_try == null).length;
  const total = rfq.quote_total_try ?? lines.reduce((s, l) => s + (l.est_cost_try ?? 0), 0);
  const fit = leadFit(rfq, now);
  const marginApproved = rfq.approved_margin_pct != null;
  const offerOpen = uncosted > 0 || fit.lead == null || !marginApproved;
  const since = daysSince(rfq.sent_at, now);
  const followUp = status === "sent" && since != null && since >= FOLLOW_UP_AFTER_DAYS;
  let offerState: JourneyState = "todo";
  let offerDetail: string = D.noLines;
  if (lines.length) {
    if (fit.lateBy) {
      offerState = "blocked";
      offerDetail = D.late(fit.lead as number, fit.lateBy);
    } else if (offerOpen) {
      offerState = "current";
      offerDetail = uncosted
        ? D.uncosted(uncosted)
        : fit.lead == null
          ? D.noLead
          : D.marginTodo(rfq.suggested_margin_pct ?? null);
    } else if (followUp) {
      offerState = "blocked";
      offerDetail = D.followUp(since as number);
    } else if (sent || closed) {
      offerState = "done";
      offerDetail = D.packed(money(total), fit.lead as number, (rfq.approved_margin_pct ?? rfq.suggested_margin_pct) as number);
    } else {
      offerState = "current";
      offerDetail = D.docReady;
    }
  }
  steps.push({
    key: "offer",
    label: L.offer,
    state: offerState,
    detail: offerDetail,
  });

  steps.push({
    key: "outcome",
    label: L.outcome,
    state: closed ? "done" : status === "sent" ? "current" : "todo",
    detail: closed ? D[status as "won" | "lost" | "cancelled"] : D.open,
  });

  return steps;
}

/** Cost, lead, margin and the sent document, as the quote page marks them. */
export function offerMarks(
  rfq: RfqRecord,
  now = Date.now(),
): Record<"cost" | "lead" | "margin" | "document", JourneyState> {
  const lines = rfq.lines ?? [];
  const uncosted = lines.filter((line) => line.est_cost_try == null).length;
  const fit = leadFit(rfq, now);
  const since = daysSince(rfq.sent_at, now);
  const status = rfqDisplayStatus(rfq);
  const sent = status === "sent" || status === "won" || status === "lost" || status === "cancelled";
  const followUp = status === "sent" && since != null && since >= FOLLOW_UP_AFTER_DAYS;
  return {
    cost: lines.length === 0 ? "todo" : uncosted > 0 ? "current" : "done",
    lead: fit.lateBy ? "blocked" : fit.lead == null ? "current" : "done",
    margin: rfq.approved_margin_pct != null ? "done" : lines.length ? "current" : "todo",
    document: followUp ? "blocked" : sent ? "done" : "todo",
  };
}

/** The first step that is current or blocked: what to do next. */
export function nextStep(steps: JourneyStep[]): JourneyStep | null {
  return steps.find((s) => s.state === "blocked") ?? steps.find((s) => s.state === "current") ?? null;
}

// ---------------------------------------------------------------- duplicates

export interface DuplicateGroup {
  partNo: string;
  /** The line to keep: the one backed by a parsed drawing, else the first. */
  keep: RfqLine;
  /** Lines folded into `keep`. */
  drop: RfqLine[];
  /** The quantity the kept line should end up with. */
  qty: number;
}

function partKey(line: RfqLine): string {
  const raw = (line.part_no || "").replace(/\.[^.]+$/, "");
  return raw.toLowerCase().replace(/[^a-z0-9çğıöşü]/g, "");
}

/**
 * The same part named twice — once by the mail or sheet, once by the
 * drawing — is the commonest intake mistake. Groups top-level lines that
 * share a part number so the desk can fold them with one click.
 */
export function duplicateGroups(rfq: RfqRecord): DuplicateGroup[] {
  const byKey = new Map<string, RfqLine[]>();
  for (const line of rfq.lines ?? []) {
    const key = partKey(line);
    if (!key) continue;
    byKey.set(key, [...(byKey.get(key) ?? []), line]);
  }
  const out: DuplicateGroup[] = [];
  for (const group of byKey.values()) {
    if (group.length < 2) continue;
    const keep = group.find((l) => l.source_job_id) ?? group[0];
    const drop = group.filter((l) => l.id !== keep.id);
    const qtys = group.map((l) => Number(l.qty) || 0).filter((q) => q > 0);
    out.push({ partNo: keep.part_no || drop[0].part_no || "", keep, drop, qty: qtys.length ? Math.max(...qtys) : 0 });
  }
  return out;
}
